import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth.service';
import { isUrlAcceptingSpecialHeaders } from '../utils';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FeatureFlagService } from '@api/services/feature-flags.service';
@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  constructor(private authService: AuthService, private translate: TranslateService, private featureFlagService: FeatureFlagService) { }


  
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    //no apikey added for webhook trigger api
    if(request.url.includes(`${environment.webhookApi}trigger`)){
      return next.handle(request);
    }
    //add engage_apigateway_key for engage api requests
    if (request.url.includes(environment.engage_apigateway_url)) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'x-api-key': environment.engage_apigateway_key
        }
      });
    } 
    //add newApiKey for all other requests except login, signup and s3 requests
    else if (request.url.indexOf('/login') === -1 && request.url.indexOf('/signup') === -1 && request.url.indexOf('s3.amazonaws.com')===-1 && request.url.indexOf('s3-accelerate.amazonaws.com')===-1) {
      const isQuoteOrBrokerRequest = request.url.startsWith(environment.newApiUrl) || request.url.startsWith(environment.newQuoteAPIEndpoint) || request.url.startsWith(environment.newQuoteAPIEndpointWithoutContext);
      const isDesignOnly = this.authService.getItem('design_only') === 'qkshodgh';
      const useNewApiKeyWithUser = !isDesignOnly && (this.featureFlagService.getFlag('use-auth-user-key') ?? false);
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'x-api-key': isQuoteOrBrokerRequest && useNewApiKeyWithUser ? environment.newApiKeyWithUser : environment.newApiKey
        }
      });
    }
    // add newApiKey only (no Bearer) for login, signup and s3 requests
    else if (request.url.indexOf('/login') != -1 || request.url.indexOf('/signup') != -1 || request.url.indexOf('s3-accelerate.amazonaws.com') != -1 || request.url.indexOf('s3.amazonaws.com') != -1 ) {
      request = request.clone({
        setHeaders: {
          'x-api-key': environment.newApiKey
        }
      });
    }
    // add s3ApiKey for 3d object api requests
    else if (request.url.indexOf('/3d-object') != -1) {
      request = request.clone({
        setHeaders: {
          'x-api-key': environment.s3ApiKey
        }
      });
    }
    // add newApiKey for all other requests
    else {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'x-api-key': environment.newApiKey
        }
      });
    }

    //return next.handle(request);
    // awaiting for the api
    return next.handle(request).pipe(
      catchError(err => { 
      if (err.status === 401 && !request.url.includes('regenerate')) {
        return this.handle401Error(request, next);
      }
      return throwError(() => err);
    }));

  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const token = this.authService.getItem('refreshToken');
      // If there's no refresh token, logout the user and return an error
      if (!token) {
        this.authService.logout();
        return throwError(() => new Error('No refresh token'));
      }
      // Attempt to refresh the token
      return this.authService.refreshToken().pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;
          const newAccessToken = response.AuthenticationResult.AccessToken;
          this.authService.setItem('token', newAccessToken);
          this.refreshTokenSubject.next(newAccessToken);
          return next.handle(
            this.addTokenHeader(request, newAccessToken)
          );
        }),

        catchError(error => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          if (error.status === 401) {
            this.showSessionExpiredPopup();
          }
          this.authService.logout();
          return throwError(() => error);
        })
      );
    }
    //if refreshing is already in progress, wait until it's done and then retry the request
    return this.refreshTokenSubject.pipe( 
      filter(token => token !== null),
      take(1),
      switchMap((token) =>
        next.handle(this.addTokenHeader(request, token))
      )
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({ headers: request.headers.set('Authorization', `Bearer ${token}`) });
  }

  private showSessionExpiredPopup() {
    const message = this.translateMsg('autoLogoutMessage')
    alert(message);
  }

  translateMsg(val: string) {
    let translatedName = ''
    this.translate.get(val).pipe(take(1)).subscribe(res => {
      translatedName = res;
    })
    return translatedName;
  }
}

export const authInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true }
];
